import pickBy from 'lodash/pickBy';

/**
 * Makes sure a value isn't undefined and isn't null.
 */
export const isDefined = (entity) =>
  typeof entity !== 'undefined' && entity !== null;

export const isFunction = (fn) => typeof fn === 'function';

/**
 * Gets a potential defined (controlled) value between arguments (controlled,
 * default, fallbacks, etc...).
 * This is useful in retrieving the preferred or initial state of something.
 *
 * For example, `defaultIsOpen` vs `isOpen`.
 *
 * @template T
 * @param {...(T|null|undefined)} args - List of potential values to check
 * @returns {T|undefined} The first defined value, or undefined if none found
 */
const getDefinedValue = (...args) => {
  let result;
  for (const arg of args) {
    if (isDefined(arg)) {
      result = arg;
      break;
    }
  }

  return result;
};

const filterUndefinedValues = (props) =>
  pickBy(props, (value) => typeof value !== 'undefined');

/**
 * @typedef {Object} AccessibleProps
 * @property {string|null} [role] - ARIA role
 * @property {boolean|null} ['aria-busy'] - Indicates element is being modified
 * @property {boolean|string|null} ['aria-checked'] - Indicates checked state
 * @property {boolean|null} ['aria-disabled'] - Indicates element is disabled
 * @property {boolean|null} ['aria-expanded'] - Indicates expanded state
 * @property {boolean|null} ['aria-grabbed'] - Indicates grabbed state
 * @property {boolean|null} ['aria-invalid'] - Indicates invalid state
 * @property {boolean|null} ['aria-pressed'] - Indicates pressed state
 * @property {boolean|null} ['aria-readonly'] - Indicates readonly state
 * @property {boolean|null} ['aria-selected'] - Indicates selected state
 * @property {boolean|null} ['data-clickable'] - Indicates element is clickable
 * @property {boolean|null} ['data-indeterminate'] - Indicates indeterminate state
 * @property {boolean|string|null} [checked] - HTML checked attribute
 * @property {boolean} [disabled] - HTML disabled attribute
 * @property {boolean|null} [invalid] - HTML invalid attribute
 * @property {boolean|null} [readOnly] - HTML readonly attribute
 * @property {boolean|string|null} [isChecked] - React checked state
 * @property {boolean|null} [isClickable] - React clickable state
 * @property {boolean|null} [isDisabled] - React disabled state
 * @property {boolean|null} [isGrabbed] - React grabbed state
 * @property {boolean|null} [isInvalid] - React invalid state
 * @property {boolean|null} [isLoading] - React loading state
 * @property {boolean|null} [isReadOnly] - React readonly state
 * @property {boolean|null} [isSelected] - React selected state
 */

/**
 * Generates accessible props based on the provided input props.
 * Handles ARIA attributes, data attributes, HTML attributes, and React state props.
 *
 * @param {Object} props - The input props
 * @param {boolean|string|null} [props.aria-busy] - ARIA busy state
 * @param {string|null} [props.aria-current] - ARIA current state
 * @param {boolean|string|null} [props.aria-checked] - ARIA checked state
 * @param {boolean|null} [props.aria-disabled] - ARIA disabled state
 * @param {boolean|null} [props.aria-expanded] - ARIA expanded state
 * @param {boolean|null} [props.aria-grabbed] - ARIA grabbed state
 * @param {boolean|null} [props.aria-pressed] - ARIA pressed state
 * @param {boolean|null} [props.aria-readonly] - ARIA readonly state
 * @param {boolean|null} [props.aria-selected] - ARIA selected state
 * @param {string|React.ReactElement} [props.as] - Element to render as
 * @param {boolean|string|null} [props.checked] - HTML checked attribute
 * @param {boolean} [props.disabled] - HTML disabled attribute
 * @param {boolean|null} [props.isClickable] - Whether element is clickable
 * @param {boolean|null} [props.invalid] - HTML invalid attribute
 * @param {boolean|string|null} [props.isChecked] - React checked state
 * @param {boolean|null} [props.isDisabled] - React disabled state
 * @param {boolean|null} [props.isExpanded] - React expanded state
 * @param {boolean|null} [props.isGrabbed] - React grabbed state
 * @param {boolean|null} [props.isIndeterminate] - React indeterminate state
 * @param {boolean|null} [props.isInvalid] - React invalid state
 * @param {boolean|null} [props.isLoading] - React loading state
 * @param {boolean|null} [props.isPressed] - React pressed state
 * @param {boolean|null} [props.isReadOnly] - React readonly state
 * @param {boolean|null} [props.isSelected] - React selected state
 * @param {boolean|null} [props.readOnly] - HTML readonly attribute
 * @param {string|null} [props.role] - ARIA role
 * @param {boolean} [props.shouldBypassAria] - Whether to bypass ARIA attributes
 * @returns {AccessibleProps} Filtered accessible props
 */
export const getAccessibleBaseProps = (props) => {
  const {
    'aria-busy': ariaBusy,
    'aria-current': ariaCurrent,
    'aria-checked': ariaChecked,
    'aria-disabled': ariaDisabled,
    'aria-expanded': ariaExpanded,
    'aria-grabbed': ariaGrabbed,
    'aria-pressed': ariaPressed,
    'aria-readonly': ariaReadonly,
    'aria-selected': ariaSelected,
    as,
    checked,
    disabled,
    isClickable: isClickableProp,
    invalid,
    isChecked: isCheckedProp,
    isDisabled: isDisabledProp,
    isExpanded: isExpandedProp,
    isGrabbed: isGrabbedProp,
    isIndeterminate: isIndeterminateProp,
    isInvalid: isInvalidProp,
    isLoading: isLoadingProp,
    isPressed: isPressedProp,
    isReadOnly: isReadOnlyProp,
    isSelected: isSelectedProp,
    readOnly,
    role: roleProp,
    shouldBypassAria,
  } = props;

  /*
   * We will occassionally want to prevent aria props from passing through to an underlying HTML node.
   * For example, with Checkbox the input should receive aria attributes, but the visual button should not.
   */
  if (shouldBypassAria) return {};

  const hasAriaCurrent = typeof ariaCurrent !== 'undefined';

  const isChecked = getDefinedValue(ariaChecked, checked, isCheckedProp);
  const isClickable = getDefinedValue(
    isClickableProp,
    as ? ['a', 'button'].includes(as) || null : null
  );
  const isDisabled = getDefinedValue(ariaDisabled, disabled, isDisabledProp);
  const isExpanded = getDefinedValue(ariaExpanded, isExpandedProp);
  const isGrabbed = getDefinedValue(ariaGrabbed, isGrabbedProp);
  const isIndeterminate = getDefinedValue(
    isChecked ? isChecked === 'mixed' : null,
    isIndeterminateProp
  );
  const isInvalid = getDefinedValue(invalid, isInvalidProp);
  const isLoading = getDefinedValue(ariaBusy, isLoadingProp);
  const isPressed = getDefinedValue(ariaPressed, isPressedProp);
  const isReadOnly = getDefinedValue(
    ariaReadonly,
    isReadOnlyProp,
    readOnly,
    null
  );
  const isSelected = getDefinedValue(ariaSelected, isSelectedProp);

  const ariaCheckedWithIndeterminate = isIndeterminate ? 'mixed' : isChecked;
  /**
   * Required... Otherwise the Spring button renders unexpectedly.
   * Uncertain as to why.
   *
   * @see https://github.com/webflow/webflow/pull/52261#issuecomment-1381113131
   *
   * 2023-01-13
   */
  const ariaDisabledValue = getDefinedValue(ariaDisabled, isDisabled);

  const buttonRole = as === 'button' ? 'button' : null;
  const role = getDefinedValue(roleProp, buttonRole);

  const accessibleProps = {
    /**
     * Aria props
     */
    'aria-busy': isLoading,
    'aria-checked': ariaCheckedWithIndeterminate,
    'aria-disabled': ariaDisabledValue,
    'aria-expanded': isExpanded,
    'aria-grabbed': isGrabbed,
    'aria-invalid': isInvalid,
    'aria-pressed': hasAriaCurrent ? null : isPressed,
    'aria-readonly': isReadOnly,
    'aria-selected': hasAriaCurrent ? null : isSelected,
    /**
     * Data props
     */
    'data-clickable': isClickable,
    'data-indeterminate': isIndeterminate,
    /**
     * HTML props
     */
    checked: isChecked,
    disabled,
    invalid: isInvalid,
    readOnly: isReadOnly,
    /**
     * React props
     */
    isChecked,
    isClickable,
    isDisabled,
    isGrabbed,
    isInvalid,
    isLoading,
    isReadOnly,
    isSelected,
    /**
     * Role
     */
    role,
  };

  return filterUndefinedValues(accessibleProps);
};
